import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import { PinIcon, SlidersIcon } from "@/assets/icons";
import { CITIES } from "@/features/services";
import { buildBoard, fmtHour } from "@/features/forecast/board";
import { useProfileStore } from "@/store/profile";
import { useCompareStore } from "@/store/compare";
import { BestCard } from "./components/BestCard";
import { HourChart } from "./components/HourChart";
import { DemandCard } from "./components/DemandCard";
import { CitySheet } from "./components/CitySheet";
import { TariffSheet } from "./components/TariffSheet";
import { CompareSkeleton } from "./components/CompareSkeleton";
import cls from "./styles.module.scss";

type SheetKind = "city" | "tariffs" | null;

export const ComparePage = () => {
  const { t } = useTranslation();
  const city = useProfileStore((s) => s.city);
  const picks = useProfileStore((s) => s.picks);
  const forecast = useCompareStore((s) => s.forecasts[city]);
  const income = useCompareStore((s) => s.income);
  const hour = useCompareStore((s) => s.hour);
  const setHour = useCompareStore((s) => s.setHour);
  const loadForecast = useCompareStore((s) => s.loadForecast);
  const loadIncome = useCompareStore((s) => s.loadIncome);
  const [sheet, setSheet] = useState<SheetKind>(null);

  useEffect(() => {
    loadForecast(city);
    loadIncome();
    // Новый город — новые данные и своё «сейчас»: курсор возвращаем на него,
    // иначе он остаётся на часе, выбранном для прошлого города.
    setHour(null);
  }, [city, loadForecast, loadIncome, setHour]);

  const board = useMemo(
    () => (forecast?.data ? buildBoard(forecast.data, city, picks) : null),
    [forecast?.data, city, picks]
  );

  const cursor = hour ?? board?.nowIndex ?? 0;
  const isForecastHour = board ? cursor > board.nowIndex : false;
  const closeSheet = useCallback(() => setSheet(null), []);
  const cityName = CITIES[city].name;

  return (
    <section className={cls.page}>
      <div className={cls.head}>
        <div>
          <span className={cls.eyebrow}>{t("compare.eyebrow", { city: cityName })}</span>
          <div className={cls.ttl}>{t("compare.title")}</div>
        </div>
        <div className={cls.freshness}>
          <div className={cls.freshA}>{t("compare.freshness")}</div>
          <div className={cls.freshB}>
            {board ? t("compare.freshnessAt", { time: board.madeLocal }) : "—"}
          </div>
        </div>
      </div>

      <div className={cls.selrow}>
        <button type="button" className={cls.selchip} onClick={() => setSheet("city")}>
          <PinIcon />
          <span className={cls.selName}>{cityName}</span>
        </button>
        <button type="button" className={cls.selchip} onClick={() => setSheet("tariffs")}>
          <SlidersIcon />
          <span className={cls.selName}>{t("compare.tariffs")}</span>
          <span className={cls.selCount}>{picks.length}</span>
        </button>
      </div>

      {forecast?.status === "error" && !board && (
        <div className={classNames(cls.card, cls.errorCard)}>
          <div className="t-body">{t("compare.error")}</div>
          <button
            type="button"
            className={cls.retry}
            onClick={() => loadForecast(city, true)}
          >
            {t("compare.retry")}
          </button>
        </div>
      )}

      {!board && forecast?.status !== "error" && <CompareSkeleton />}

      {board && (
        <>
          <BestCard board={board} hour={cursor} cityName={cityName} />

          <div className={cls.card}>
            <div className={cls.sechead}>
              <span className={cls.secTitle}>{t("compare.perHour")}</span>
              <span className={cls.hourbadge}>
                <b>{fmtHour(board.hours[cursor])}</b>
                <span style={{ color: isForecastHour ? "var(--accent)" : "var(--ink-3)" }}>
                  {isForecastHour ? t("compare.forecast") : t("compare.fact")}
                </span>
              </span>
            </div>
            <HourChart board={board} hour={cursor} onHour={setHour} />
          </div>

          <DemandCard
            board={board}
            hour={cursor}
            city={city}
            income={income.data}
            status={income.status}
          />
        </>
      )}

      <CitySheet isOpen={sheet === "city"} onClose={closeSheet} />
      <TariffSheet isOpen={sheet === "tariffs"} onClose={closeSheet} />
    </section>
  );
};
